<template>
    <div id="order-line-overview">
        <pre
            v-if="isDebug"
            class="debug"
            >{{
                {
                    selectedOrderLines: selectedOrderLines.map(
                        (x) => x.orderlineId
                    ),
                }
            }}</pre
        >
        <div class="card orderLines-list">
            <div class="card-content">
                <div class="top-buttons">
                    <actions-menu v-if="hasRight('productionmanagement')">
                        <actions-menu-item
                            :v4="true"
                            :action="exportOrderlinesWithoutPhotoWithFilter"
                            icon="file-pdf-o"
                            :label="
                                trans(
                                    'pages.orderlineoverview.export_items_without_photo'
                                )
                            "
                        />
                        <actions-menu-item
                            :v4="true"
                            :action="exportOrderlinesWithPhotoWithFilter"
                            icon="file-pdf-o"
                            :label="
                                trans(
                                    'pages.orderlineoverview.export_items_with_photo'
                                )
                            "
                        />
                        <actions-menu-item
                            :v4="true"
                            :action="exportPurchaseOrder"
                            icon="file-pdf-o"
                            :label="
                                trans('pages.orderlineoverview.purchase_order')
                            "
                        />
                    </actions-menu>

                    <!-- Commented out because of: EOSOL-4991 -->
                    <!-- <ui5-button
                        v-if="hasRight('productionmanagement')"
                        @click="generateOrderLines"
                        @mouseover="
                            $refs.tooltipGenerateOrderLines.showAt($event.target)
                        "
                        @mouseout="$refs.tooltipGenerateOrderLines.close()"
                    >
                        {{ trans('pages.orderlineoverview.auto_generate') }}
                    </ui5-button>
                    <ui5-popover
                        ref="tooltipGenerateOrderLines"
                        :allow-target-overlap="false"
                        placement-type="Bottom">
                        <div>
                            {{ trans('pages.orderlineoverview.auto_generate_tooltip') }}
                        </div>
                    </ui5-popover> -->

                    <ui5-button
                        v-if="hasRight('productionmanagement')"
                        @click="exportOrderLines"
                        @mouseover="
                            $refs.tooltipCsvExport.showAt($event.target)
                        "
                        @mouseout="$refs.tooltipCsvExport.close()"
                    >
                        {{ trans('pages.orderlineoverview.csv_export') }}
                    </ui5-button>
                    <ui5-popover
                        ref="tooltipCsvExport"
                        :allow-target-overlap="false"
                        placement-type="Bottom"
                    >
                        <div>
                            {{
                                trans(
                                    'pages.orderlineoverview.csv_export_tooltip'
                                )
                            }}
                        </div>
                    </ui5-popover>
                </div>
                <crm-table
                    ref="orderLineTable"
                    :columns="columns"
                    :items="orderLines"
                    :count="orderLines.length + 1"
                    :status="status"
                    @fetch="onFetchDebounced"
                    :default-filters="initialFilters"
                    default-sort="created"
                    default-order="desc"
                    :has-sticky="true"
                    :use-global-scroll="true"
                    :tr-click="trClick"
                    :get-key="(item) => item.orderlineId"
                >
                    <template v-slot:no-results>
                        {{
                            trans('pages.orderlineoverview.no_orderlines_found')
                        }}
                    </template>
                    <template v-slot:filter-supplier="props">
                        <auto-complete-input
                            v-if="supplierOptions"
                            class="filter"
                            :placeholder="props.column.label"
                            :value="props.value"
                            @input="props.onInput(props.column.name, $event)"
                            :options="supplierOptions"
                            :lbl-no-options="
                                trans(
                                    'pages.orderlineoverview.no_suppliers_found'
                                )
                            "
                        />
                    </template>
                    <template v-slot:filter-status="props">
                        <ui5-select
                            class="filter"
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.selectedOption.value
                                )
                            "
                        >
                            <ui5-option
                                v-for="[value, label] in Object.entries(
                                    statuses
                                )"
                                :value="value"
                                :selected="props.value == value"
                                :key="'status.' + value"
                            >
                                {{ label }}
                            </ui5-option>
                        </ui5-select>
                    </template>
                    <template v-slot:filter-clearFilters="props">
                        <ui5-button @click="clearFilters">
                            {{ trans('pages.orderlineoverview.reset') }}
                        </ui5-button>
                    </template>
                    <template v-slot:selection="props">
                        <td>
                            <div class="checkbox">
                                <input
                                    v-model="props.item.selected"
                                    type="checkbox"
                                    :name="'selection-' + props.item.productId"
                                    :true-value="true"
                                    :false-value="false"
                                />
                                <label
                                    :for="'selection-' + props.item.productId"
                                ></label>
                            </div>
                        </td>
                    </template>
                    <template v-slot:actions="props">
                        <td>
                            <div>
                                <template
                                    v-if="hasRight('productionmanagement')"
                                >
                                    <i
                                        class="fa fa-print"
                                        @click.stop="openPrintModal(props.item)"
                                    />
                                    <i
                                        class="fa fa-info-circle"
                                        @click.stop="
                                            $router.push(
                                                '/profilepage/' +
                                                    props.item.profile.profileId
                                            )
                                        "
                                    />
                                    <i
                                        class="fa fa-times"
                                        @click.stop="
                                            remove(props.item, props.itemIndex)
                                        "
                                    />
                                </template>
                            </div>
                        </td>
                    </template>
                    <template v-slot:profile="props">
                        <td>
                            {{ props.item.profile.code }}
                            <template v-if="props.item.profile.description">
                                {{ props.item.profile.description }}
                            </template>
                        </td>
                    </template>
                    <template v-slot:amountOrdered="props">
                        <td v-if="!clickToEdit || props.item.openForEdit">
                            <text-input
                                class="orderline-amountordered"
                                type="number"
                                :disabled="!hasRight('productionmanagement')"
                                @blur="updateOrderline(props.item)"
                                @click.stop="() => {}"
                                v-model="props.item.amountOrdered"
                            />
                        </td>
                        <td v-else>
                            {{ props.item.amountOrdered }}
                        </td>
                    </template>
                    <template v-slot:profileUnit="props">
                        <td>
                            {{ props.item.profile.unit }}
                        </td>
                    </template>
                    <template v-slot:profilePackagePerUnit="props">
                        <td>
                            {{ props.item.profile.packagePerUnit }}
                        </td>
                    </template>
                    <template v-slot:supplier="props">
                        <td>
                            {{ props.item.profile.supplier }}
                        </td>
                    </template>
                    <template v-slot:profileSupplierProfileCode="props">
                        <td>
                            {{ props.item.profile.supplierProfileCode }}
                        </td>
                    </template>
                    <template v-slot:minStock="props">
                        <td>
                            {{ props.item.profile.minStock }}
                        </td>
                    </template>
                    <template v-slot:maxStock="props">
                        <td>
                            {{ props.item.profile.maxStock }}
                        </td>
                    </template>
                    <template v-slot:currentStockCount="props">
                        <td>
                            {{ props.item.profile.currentstockcount }}
                        </td>
                    </template>
                    <template v-slot:spareStockCount="props">
                        <td>
                            {{ props.item.profile.sparestock }}
                        </td>
                    </template>
                    <template v-slot:profileNetto="props">
                        <td v-if="hasRight('administration')">
                            {{ formatPrice(props.item.profile.netto) }}
                        </td>
                        <td v-else>-</td>
                    </template>
                    <template v-slot:created="props">
                        <td>
                            {{ getSlashDate(props.item.created) }}
                        </td>
                    </template>
                    <template v-slot:employee="props">
                        <td>
                            {{ props.item.employee.name }}
                        </td>
                    </template>
                    <template v-slot:status="props">
                        <td v-if="!clickToEdit || props.item.openForEdit">
                            <ui5-select
                                @change="
                                    props.item.status =
                                        $event.detail.selectedOption.value;
                                    updateOrderline(props.item);
                                "
                            >
                                <ui5-option
                                    v-for="[value, label] in Object.entries(
                                        statuses
                                    )"
                                    :value="value"
                                    :disabled="
                                        !hasRight('productionmanagement')
                                    "
                                    :selected="props.item.status == value"
                                    :key="'status.' + value"
                                >
                                    {{ label }}
                                </ui5-option>
                            </ui5-select>
                        </td>
                        <td v-else>{{ statuses[props.item.status] }}</td>
                    </template>
                    <template v-slot:dateOrdered="props">
                        <td v-if="!clickToEdit || props.item.openForEdit">
                            <ui5-input
                                class="dateordered-input"
                                type="Text"
                                :disabled="!hasRight('productionmanagement')"
                                :id="'editdateordered' + props.item.orderlineId"
                                :value="props.item.dateOrdered"
                                @input="
                                    props.item.dateOrdered = $event.target.value
                                "
                                @blur="updateDateOrdered(props.item)"
                                @click.stop="() => {}"
                            />
                        </td>
                        <td v-else>{{ props.item.dateOrdered }}</td>
                    </template>
                    <template v-slot:deliveryDate="props">
                        <td v-if="!clickToEdit || props.item.openForEdit">
                            <ui5-input
                                class="deliverydate-input"
                                type="Text"
                                :disabled="!hasRight('productionmanagement')"
                                :id="
                                    'editdeliverydate' + props.item.orderlineId
                                "
                                :value="props.item.deliveryDate"
                                @input="
                                    props.item.deliveryDate =
                                        $event.target.value
                                "
                                @blur="updateDeliveryDate(props.item)"
                                @click.stop="() => {}"
                            />
                        </td>
                        <td v-else>{{ props.item.deliveryDate }}</td>
                    </template>
                    <template
                        v-slot:footer
                        v-if="status === 'success'"
                    >
                        <tr>
                            <th class="sticky">
                                <div class="multiselect-actions">
                                    <div class="checkbox">
                                        <input
                                            id="select-all"
                                            @change="selectAll"
                                            type="checkbox"
                                            name="select-all"
                                            :true-value="true"
                                            :false-value="false"
                                        />
                                        <label for="select-all"></label>
                                    </div>
                                    <template
                                        v-if="selectedOrderLines.length > 0"
                                    >
                                        <div
                                            @click="orderSelectedOrderlines"
                                            class="multiselect-action"
                                        >
                                            <i class="fa fa-shopping-cart" />
                                            {{
                                                trans(
                                                    'pages.orderlineoverview.order'
                                                )
                                            }}
                                        </div>
                                        <div
                                            @click="exportTestas"
                                            class="multiselect-action"
                                        >
                                            <i class="fa fa-download" />
                                            {{
                                                trans(
                                                    'pages.orderlineoverview.export_testas'
                                                )
                                            }}
                                        </div>
                                        <div
                                            @click="orderTestas"
                                            class="multiselect-action"
                                        >
                                            <i class="fa fa-download" />
                                            {{
                                                trans(
                                                    'pages.orderlineoverview.place_testas_order'
                                                )
                                            }}
                                        </div>
                                    </template>
                                </div>
                            </th>
                        </tr>
                    </template>
                </crm-table>
            </div>
        </div>
        <modal
            v-if="printModal.orderLine"
            :show="printModal.show"
            :loading="printModal.loading"
            :actions="printModal.actions"
            @close="printModal.show = false"
            :title="
                printModal.orderLine.profile
                    ? trans('pages.orderlineoverview.print_kanban_for', {
                          code: printModal.orderLine.profile.code,
                      })
                    : trans('pages.orderlineoverview.print_kanban')
            "
        >
            <p class="text-base">
                <span v-if="printModal.orderLine.profile">
                    {{
                        trans(
                            'pages.orderlineoverview.confirm_print_kanban_for',
                            { code: printModal.orderLine.profile.code }
                        )
                    }}
                </span>
                <span v-else>
                    {{ trans('pages.orderlineoverview.confirm_print_kanban') }}
                </span>
            </p>
            <p class="text-sm mt-2">
                {{ trans('pages.orderlineoverview.status_update') }}
            </p>
            <div class="mt-5 text-base">
                <p>{{ trans('pages.orderlineoverview.choose_stock') }}</p>
                <div class="flex items-center gap-2 mt-2">
                    <input
                        id="addToSpareStock"
                        type="radio"
                        v-model="printModal.destinationStock"
                        value="current"
                        class="cursor-pointer"
                    />
                    <label
                        for="addToSpareStock"
                        class="cursor-pointer"
                    >
                        {{ trans('pages.orderlineoverview.current_stock') }}
                    </label>
                </div>
                <div class="flex items-center gap-2 mt-2">
                    <input
                        id="addToCurrentStock"
                        type="radio"
                        v-model="printModal.destinationStock"
                        value="spare"
                        class="cursor-pointer"
                    />
                    <label
                        for="addToCurrentStock"
                        class="cursor-pointer"
                    >
                        {{ trans('pages.orderlineoverview.spare_stock') }}
                    </label>
                </div>
            </div>
        </modal>
    </div>
</template>
<script>
import IsDebugMixin from '../mixins/is-debug';
import Spinner from '../components/loader/Spinner';
import Modal from '../components/modals/Modal';
import {
    clearedFilters,
    pattern,
    statuses,
} from './../shared/orderLineOverview';
import CrmTable from '../components/CrmTable';
import TextInput from '../components/TextInput';
import AutoCompleteInput from '../components/AutoCompleteInput';
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import orderLineApi from '../api/orderline';
import orderlineApi from '../api/orderline';
import api from '../api/api';
import axios from 'axios';
import exportApi from '../api/export';
import supplierApi from '../api/supplier';
import profileApi from '../api/profile';
import powderApi from '../api/powder';
import { mapGetters } from 'vuex';
import {
    showConfirmDialog,
    showErrorMessage,
    showInfoMessage,
} from '../dialogs';
import _ from 'lodash';
import { saveAs } from 'file-saver';
import { getSlashDate } from '../helpers/DateHelper';
import localization from '../mixins/localization';
import CurrencyHelper from '../helpers/CurrencyHelper';

export default {
    mixins: [IsDebugMixin, localization],
    components: {
        Spinner,
        CrmTable,
        TextInput,
        AutoCompleteInput,
        ActionsMenu,
        ActionsMenuItem,
        Modal,
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
            clickToEdit: 'appSettings/getClickToEditOrderLineOverview',
        }),
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
        statuses: () => statuses,
        onFetchDebounced() {
            return _.debounce(this.onFetch, 500);
        },
        selectedOrderLines() {
            if (!this.orderLines) {
                return [];
            }

            return this.orderLines.filter((x) => x.selected);
        },
    },
    data() {
        return {
            initialFilters: { ...clearedFilters },
            status: 'running',
            orderLines: [],
            supplierOptions: null,
            printModal: {
                show: false,
                loading: false,
                orderLine: null,
                destinationStock: 'current',
                actions: [
                    {
                        label: this.trans('pages.orderlineoverview.no'),
                        handler: () => {
                            this.printModal.show = false;
                        },
                    },
                    {
                        label: this.trans('pages.orderlineoverview.yes'),
                        theme: 'primary',
                        handler: () => {
                            this.print(
                                this.printModal.orderLine,
                                this.printModal.destinationStock
                            );
                            this.printModal.show = false;
                        },
                    },
                ],
            },
        };
    },
    methods: {
        getSlashDate,
        formatPrice(value) {
            return CurrencyHelper.formatPrice(value, this.currentLocaleIso2Code());
        },
        createColumns() {
            return [
                {
                    name: 'selection',
                    label: '',
                },
                {
                    name: 'actions',
                    label: this.trans('pages.orderlineoverview.actions'),
                },
                {
                    name: 'profile',
                    label: this.trans('pages.orderlineoverview.article'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'amount',
                    label: this.trans('pages.orderlineoverview.amount'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'amountOrdered',
                    label: this.trans('pages.orderlineoverview.to_order'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'profileUnit',
                    label: this.trans('pages.orderlineoverview.unit'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'profilePackagePerUnit',
                    label: this.trans('pages.orderlineoverview.packaging'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'comments',
                    label: this.trans('pages.orderlineoverview.comments'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'supplier',
                    label: this.trans('pages.orderlineoverview.supplier'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'profileSupplierProfileCode',
                    label: this.trans(
                        'pages.orderlineoverview.supplier_article_number'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'minStock',
                    label: this.trans('pages.orderlineoverview.min_stock'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'maxStock',
                    label: this.trans('pages.orderlineoverview.max_stock'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'currentStockCount',
                    label: this.trans('pages.orderlineoverview.current_stock'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'spareStockCount',
                    label: this.trans('pages.orderlineoverview.spare_stock'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'profileNetto',
                    label: this.trans('pages.orderlineoverview.price'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'created',
                    label: this.trans('pages.orderlineoverview.date'),
                    searchable: false,
                    sortable: false,
                },
                {
                    name: 'employee',
                    label: this.trans('pages.orderlineoverview.employee'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'status',
                    label: this.trans('pages.orderlineoverview.status'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'dateOrdered',
                    label: this.trans('pages.orderlineoverview.date_ordered'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'deliveryDate',
                    label: this.trans('pages.orderlineoverview.delivery_date'),
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'clearFilters',
                    label: 'clearFilters',
                    searchable: true,
                    hideColumn: true,
                },
            ];
        },
        exportTestas() {
            if (this.selectedOrderLines.length == 0) {
                return showErrorMessage(
                    this.trans(
                        'pages.orderlineoverview.select_orderlines_to_export'
                    )
                );
            }

            orderLineApi
                .exportTestas(this.selectedOrderLines.map((x) => x.orderlineId))
                .then((response) => {
                    showInfoMessage(
                        this.trans('pages.orderlineoverview.export_successful')
                    );
                    for (const [filename, data] of Object.entries(
                        response.data.data
                    )) {
                        const blob = new Blob([data], {
                            type: 'Content-type: text/xml;charset=utf-8;',
                        });
                        saveAs(blob, filename);

                        const promises = this.selectedOrderLines.map(
                            (orderLine) => {
                                return orderlineApi
                                    .setStatusOrdered(orderLine.orderlineId)
                                    .then(() => {
                                        orderLine.selected = false;
                                    });
                            }
                        );

                        Promise.all(promises).then(() => {
                            showInfoMessage(
                                this.trans(
                                    'pages.orderlineoverview.lines_marked_as_ordered'
                                )
                            );
                            this.load();
                        });
                    }
                })
                .catch((error) => {
                    const code = error.response.data.code;
                    if (code == 'notAllTestas') {
                        return showErrorMessage(
                            this.trans(
                                'pages.orderlineoverview.all_lines_must_be_testas'
                            )
                        );
                    }
                    showErrorMessage(
                        this.trans(
                            'pages.orderlineoverview.unknown_error_testas_export'
                        )
                    );
                });
        },
        orderTestas() {
            if (this.selectedOrderLines.length == 0) {
                return showErrorMessage(
                    this.trans(
                        'pages.orderlineoverview.select_orderlines_to_export'
                    )
                );
            }

            orderLineApi
                .orderTestas(this.selectedOrderLines.map((x) => x.orderlineId))
                .then((response) => {
                    showInfoMessage(
                        this.trans('pages.orderlineoverview.order_successful')
                    );
                    for (const [filename, data] of Object.entries(
                        response.data.data
                    )) {
                        const promises = this.selectedOrderLines.map(
                            (orderLine) => {
                                return orderlineApi
                                    .setStatusOrdered(orderLine.orderlineId)
                                    .then(() => {
                                        orderLine.selected = false;
                                    });
                            }
                        );

                        Promise.all(promises).then(() => {
                            showInfoMessage(
                                this.trans(
                                    'pages.orderlineoverview.lines_marked_as_ordered'
                                )
                            );
                            this.load();
                        });
                    }
                })
                .catch((error) => {
                    const code = error.response.data.code;
                    if (code == 'notAllTestas') {
                        return showErrorMessage(
                            this.trans(
                                'pages.orderlineoverview.all_lines_must_be_testas_order'
                            )
                        );
                    }
                    showErrorMessage(
                        this.trans(
                            'pages.orderlineoverview.unknown_error_testas_order'
                        )
                    );
                });
        },
        trClick(item) {
            this.orderLines.forEach((x) => {
                x.openForEdit = false;
            });
            item.openForEdit = true;
        },
        selectAll($event) {
            const value = $event.target.checked;
            this.orderLines.forEach((x) => {
                x.selected = value;
            });
        },
        orderSelectedOrderlines() {
            if (this.selectedOrderLines.length == 0) {
                return showErrorMessage(
                    this.trans(
                        'pages.orderlineoverview.select_orderlines_to_order'
                    )
                );
            }

            const suppliers = this.selectedOrderLines.map(
                (x) => x.profile.supplierId
            );
            const uniqueSuppliers = [...new Set(suppliers)];

            if (uniqueSuppliers.length != 1) {
                return showErrorMessage(
                    this.trans(
                        'pages.orderlineoverview.select_orderlines_same_supplier'
                    )
                );
            }

            window.open(
                Config.url +
                    '/export/exportPurchaseOrderForSelectedOrderlines/?&orderlinesArr=' +
                    this.selectedOrderLines.map((x) => x.orderlineId),
                '_blank'
            );

            const promises = this.selectedOrderLines.map((orderLine) => {
                return orderlineApi
                    .setStatusOrdered(orderLine.orderlineId)
                    .then(() => {
                        orderLine.selected = false;
                    });
            });

            Promise.all(promises).then(() => {
                this.load();
            });
        },
        clearFilters() {
            this.$refs.orderLineTable.clearFilters();
        },
        updateDateOrdered(orderLine) {
            const dateOrdered = orderLine.dateOrdered;
            if (dateOrdered) {
                if (dateOrdered.match(pattern)) {
                    orderlineApi
                        .updateDateOrdered(orderLine.orderlineId, dateOrdered)
                        .then(() => {
                            showInfoMessage(
                                this.trans(
                                    'pages.orderlineoverview.order_date_updated'
                                )
                            );
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.orderlineoverview.error_saving_order_date'
                                )
                            );
                        });
                } else {
                    showInfoMessage(
                        this.trans(
                            'pages.orderlineoverview.enter_order_date_correctly'
                        )
                    );
                }
            }
        },
        updateDeliveryDate(orderLine) {
            const deliveryDate = orderLine.deliveryDate;
            if (deliveryDate) {
                if (deliveryDate.match(pattern)) {
                    orderlineApi
                        .updateDeliveryDate(orderLine.orderlineId, deliveryDate)
                        .then(() => {
                            showInfoMessage(
                                this.trans(
                                    'pages.orderlineoverview.delivery_date_updated'
                                )
                            );
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.orderlineoverview.error_saving_delivery_date'
                                )
                            );
                        });
                } else {
                    showInfoMessage(
                        this.trans(
                            'pages.orderlineoverview.enter_delivery_date_correctly'
                        )
                    );
                }
            }
        },
        exportOrderlinesWithoutPhotoWithFilter() {
            const profile = this.initialFilters.profile;
            const notes = this.initialFilters.notes;
            const supplier = this.initialFilters.supplier;
            const bin = this.initialFilters.bin;
            const status = this.initialFilters.status;
            const sortField = this.initialFilters.sort;
            const sortOrder = this.initialFilters.order;
            const productNr = this.initialFilters.productNr;
            window.open(
                Config.url +
                    '/export/exportProfilesForOrderline/?showimages=0&profile=' +
                    profile +
                    '&notes=' +
                    notes +
                    '&supplier=' +
                    supplier +
                    '&bin=' +
                    bin +
                    '&status=' +
                    status +
                    '&sortField=' +
                    sortField +
                    '&sortOrder=' +
                    sortOrder +
                    '&productNr=' +
                    productNr,
                '_blank'
            );
        },
        exportOrderlinesWithPhotoWithFilter() {
            const profile = this.initialFilters.profile;
            const notes = this.initialFilters.notes;
            const supplier = this.initialFilters.supplier;
            const bin = this.initialFilters.bin;
            const status = this.initialFilters.status;
            const sortField = this.initialFilters.sort;
            const sortOrder = this.initialFilters.order;
            const productNr = this.initialFilters.productNr;
            window.open(
                Config.url +
                    '/export/exportProfilesForOrderline/?showimages=1&profile=' +
                    profile +
                    '&notes=' +
                    notes +
                    '&supplier=' +
                    supplier +
                    '&bin=' +
                    bin +
                    '&barcode=' +
                    status +
                    '&sortField=' +
                    sortField +
                    '&sortOrder=' +
                    sortOrder +
                    '&productNr=' +
                    productNr,
                '_blank'
            );
        },
        exportPurchaseOrder() {
            const profile = this.initialFilters.profile;
            const notes = this.initialFilters.notes;
            const supplier = this.initialFilters.supplier;
            const bin = this.initialFilters.bin;
            const status = this.initialFilters.status;
            const sortField = this.initialFilters.sort;
            const sortOrder = this.initialFilters.order;
            const productNr = this.initialFilters.productNr;
            window.open(
                Config.url +
                    '/export/exportPurchaseOrder/?showimages=1&profile=' +
                    profile +
                    '&notes=' +
                    notes +
                    '&supplier=' +
                    supplier +
                    '&bin=' +
                    bin +
                    '&status=' +
                    status +
                    '&sortField=' +
                    sortField +
                    '&sortOrder=' +
                    sortOrder +
                    '&productNr=' +
                    productNr,
                '_blank'
            );
        },
        generateOrderLines() {
            orderlineApi.generateOrderlines().then(() => {
                this.$refs.orderLineTable.fetch();
            });
        },
        exportOrderLines() {
            const url = api.createGetUrl('/orderline/export/', {
                supplier: this.initialFilters.supplier,
                status: this.initialFilters.status,
            });
            window.open(url);
        },
        onFetch(filters) {
            this.status = 'running';

            const payload = {
                ...filters,
                notes: filters.comments,
                sortField: filters.sort
                    .replace('amountOrdered', 'amountordered')
                    .replace('profileUnit', 'profileunit')
                    .replace('profilePackagePerUnit', 'profilepackageperunit')
                    .replace('supplier', 'profilesupplier')
                    .replace(
                        'profileSupplierProfileCode',
                        'profilesupplierprofilecode'
                    )
                    .replace('bin', 'profilebin')
                    .replace('minStock', 'profileminstock')
                    .replace('maxStock', 'profilemaxstock')
                    .replace('currentStockCount', 'profilecurrentstockcount')
                    .replace('sparesStockCount', 'profilesparestockcount')
                    .replace('profileNetto', 'profilenetto')
                    .replace('dateOrdered', 'dateordered')
                    .replace('deliveryDate', 'deliveryDate'),
                sortOrder: filters.order.toUpperCase(),
            };

            this.$store.commit('orderLineOverview/setFilters', filters, {
                root: true,
            });
            if (payload.page == 1) {
                this.orderLines = [];
            }
            orderLineApi
                .list(payload)
                .then((response) => {
                    const orderLines = response.data.data;

                    orderLines.forEach((orderLine) => {
                        orderLine.selected = false;
                        orderLine.marked = false;
                        orderLine.openForEdit = false;
                        this.processOrderLine(orderLine);
                    });

                    this.orderLines = [...this.orderLines, ...orderLines];

                    this.status = 'success';
                })
                .catch((err) => {
                    if (!axios.isCancel(err)) {
                        this.status = 'error';
                    }
                });
        },
        processOrderLine(orderLine) {
            orderLine.selected = false;
        },
        openPrintModal(orderLine) {
            this.printModal.orderLine = orderLine;
            this.printModal.loading = true;

            profileApi
                .isPowder(orderLine.profile.profileId)
                .then((response) => {
                    const profile = response.data.data;
                    const isPowder = profile.isPowder;
                    if (isPowder) {
                        this.printModal.destinationStock = 'spare';
                    } else {
                        this.printModal.destinationStock = 'current';
                    }

                    this.printModal.loading = false;
                });

            this.printModal.show = true;
        },
        print(orderLine, destinationStock = 'current') {
            orderLine.status = 6;
            this.updateOrderline(orderLine, destinationStock).then(() => {
                KeyHelper.handlePrintOrExport(
                    () => exportApi
                        .printProfileKanbanLabel(
                            orderLine.profile.profileId,
                            this.selectedPrinterPostParameter
                        )
                        .then(() => {
                            showInfoMessage(
                                this.trans('pages.orderlineoverview.label_printed')
                            );
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans('pages.orderlineoverview.error_printing_label')
                            );
                        }),
                    Config.url +
                        '/export/exportProfileKanbanLabel/' +
                        '?profileId=' +
                        orderLine.profile.profileId
                );
            });
        },
        remove(orderLine, index) {
            showConfirmDialog({
                title: this.trans('pages.orderlineoverview.remove_line'),
                content: this.trans(
                    'pages.orderlineoverview.confirm_remove_line'
                ),
                confirm: () => {
                    orderlineApi
                        .remove(orderLine.orderlineId)
                        .then(() => {
                            this.$delete(this.orderLines, index);
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.orderlineoverview.remove_failed'
                                )
                            );
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans('pages.orderlineoverview.yes'),
                cancelButton: this.trans('pages.orderlineoverview.no'),
            });
        },
        updateOrderline(orderLine, destinationStock = 'current') {
            return new Promise((resolve, reject) => {
                const orderlineId = orderLine.orderlineId;
                const amount = orderLine.amount;
                const amountOrdered = orderLine.amountOrdered;
                const comments = orderLine.comments;
                const status = orderLine.status;

                orderlineApi
                    .update(
                        orderlineId,
                        amount,
                        amountOrdered,
                        comments,
                        status,
                        destinationStock
                    )
                    .then(() => {
                        orderlineApi
                            .details(orderlineId)
                            .then((response) => {
                                orderLine = {
                                    ...orderLine,
                                    ...this.processOrderLine(
                                        response.data.data
                                    ),
                                };
                                if (status == '6') {
                                    //orderline completely delivered
                                    this.updateProductsForPowder(
                                        orderLine.profile.profileId
                                    )
                                        .then(() => {
                                            resolve();
                                        })
                                        .catch(() => {
                                            reject();
                                        });
                                } else {
                                    resolve();
                                }
                            })
                            .catch(() => {
                                showErrorMessage(
                                    this.trans(
                                        'pages.orderlineoverview.error_fetching_orderline_details'
                                    )
                                );
                                reject();
                            });
                    });
            });
        },
        updateProductsForPowder(profileId) {
            return new Promise((resolve, reject) => {
                profileApi
                    .isPowder(profileId)
                    .then((response) => {
                        const profile = response.data.data;
                        const isPowder = profile.isPowder;
                        if (isPowder) {
                            showConfirmDialog({
                                title: this.trans(
                                    'pages.orderlineoverview.update_product'
                                ),
                                content: this.trans(
                                    'pages.orderlineoverview.update_powder_products'
                                ),
                                confirm: (dialog) => {
                                    dialog.close();
                                    powderApi
                                        .updatePowderDelivered(profileId)
                                        .then(() => {
                                            resolve();
                                        })
                                        .catch(() => {
                                            showErrorMessage(
                                                this.trans(
                                                    'pages.orderlineoverview.powder_update_failed'
                                                )
                                            );
                                            reject();
                                        });
                                },
                                cancel: (dialog) => {
                                    resolve();
                                    dialog.close();
                                },
                                confirmButton: this.trans(
                                    'pages.orderlineoverview.update'
                                ),
                                cancelButton: this.trans(
                                    'pages.orderlineoverview.cancel'
                                ),
                            });
                        } else {
                            resolve();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },
    },
    created() {
        this.columns = this.createColumns();
    },
    mounted() {
        this.initialFilters =
            this.$store.getters['orderLineOverview/getFilters'];
        supplierApi.fetchAllSuppliers().then((response) => {
            this.supplierOptions = response.data.data.map((x) => {
                return {
                    label: x.name,
                    value: x.name,
                };
            });
        });
    },
};
</script>
